import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Heading,
	HStack,
	Stack,
	Text,
	Divider,
} from '@chakra-ui/react';
import BasicItemCard from 'components/Item/BasicItemCard/BasicItemCard';
import ItemGrid from 'components/Item/ItemGrid/ItemGrid';
import { DedupItemModel, DedupStatus, DedupItemCandidateModel } from 'models/Dedup/DedupItem';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationService } from 'services/Navigation';
import { useCreateMerge } from 'hooks/queries/useMerges';
import { useDeleteDedupItem, useDeleteDedupItemPair } from 'hooks/queries/useDedup';
import { ItemModel } from 'models';

interface DedupItemCardProps {
	dedupItem: DedupItemModel;
}

function getStatusColor(status: string): string {
	switch (status) {
		case DedupStatus.APPROVED:
			return 'green';
		case DedupStatus.REJECTED:
			return 'red';
		default:
			return 'orange';
	}
}

function formatSimilarityLabel(value?: number): string {
	if (value === undefined || value === null) {
		return 'N/A';
	}
	return `${Math.round(value * 10000) / 10000}`;
}

function getSimilarityColor(value?: number): string | undefined {
	if (value === undefined || value === null) {
		return undefined;
	}

	if (value === 1) {
		return 'white';
	}

	if (value > 0.95) {
		return 'purple.600';
	}

	return 'purple.400';
}

function getSimilarityVariant(value?: number): 'subtle' | 'solid' | 'outline' | undefined {
	if (value === undefined || value === null) {
		return undefined;
	}

	if (value === 1) {
		return 'solid';
	}

	return 'subtle';
}

function getSimilarityColorScheme(value?: number): string | undefined {
	if (value === undefined || value === null) {
		return undefined;
	}

	if (value === 1) {
		return 'green';
	}

	return 'purple';
}

export default function DedupItemCard({ dedupItem }: DedupItemCardProps) {
	const navigate = useNavigate();
	const duplicates = useMemo(() => dedupItem.candidates || [], [dedupItem.candidates]);
	const [deletingCandidateId, setDeletingCandidateId] = useState<string | null>(null);
	const createMergeMutation = useCreateMerge();
	const deleteDedupItemMutation = useDeleteDedupItem();
	const deleteDedupItemPairMutation = useDeleteDedupItemPair();
	const isPending = dedupItem.isPending();

	const mergeItemIds = useMemo(() => {
		const ids = new Set<string>();

		if (dedupItem.item && dedupItem.item.id) {
			ids.add(dedupItem.item.id);
		}

		duplicates.forEach((candidate: DedupItemCandidateModel) => {
			if (candidate.item && candidate.item.id) {
				ids.add(candidate.item.id);
			}
		});

		return Array.from(ids);
	}, [dedupItem.item, duplicates]);

	const hasSufficientItems = mergeItemIds.length >= 2;
	const primaryItemId = dedupItem.item?.id;

	const candidateItems = useMemo(() => {
		return duplicates
			.map((candidate) => candidate.item)
			.filter((item): item is ItemModel => !!item);
	}, [duplicates]);

	const averageSimilarity = useMemo(() => {
		const similarities = duplicates
			.map((candidate) => candidate.similarity)
			.filter((value): value is number => typeof value === 'number');

		if (!similarities.length) {
			return undefined;
		}

		const total = similarities.reduce((sum, value) => sum + value, 0);
		return total / similarities.length;
	}, [duplicates]);

	const averageSimilarityLabel = averageSimilarity !== undefined ? formatSimilarityLabel(averageSimilarity) : 'N/A';
	const averageColorScheme = averageSimilarity !== undefined ? getSimilarityColorScheme(averageSimilarity) : 'gray';
	const averageVariant = averageSimilarity !== undefined ? getSimilarityVariant(averageSimilarity) : 'subtle';

	const candidateGridMaxHeight = Math.max(400, candidateItems.length * 200);

	const handlePrimaryItemClick = () => {
		if (dedupItem.item?.id) {
			navigate(NavigationService.getItemDetailsPath(dedupItem.item.id, dedupItem.item.name));
		}
	};

	const handleCandidateClick = (candidate: DedupItemCandidateModel) => {
		if (candidate.item?.id) {
			navigate(NavigationService.getItemDetailsPath(candidate.item.id, candidate.item.name));
		}
	};

	const handleCreateMerge = () => {
		if (!hasSufficientItems || !primaryItemId) {
			return;
		}

		createMergeMutation.mutate(
			{
				items: mergeItemIds,
			},
			{
				onSuccess: () => {
					const targetItemId = primaryItemId || dedupItem.id;
					if (targetItemId) {
						deleteDedupItemMutation.mutate(targetItemId);
					}
				},
			},
		);
	};

	const handleReject = () => {
		if (!primaryItemId) {
			return;
		}

		deleteDedupItemMutation.mutate(primaryItemId);
	};

	const handleDeleteCandidate = (candidate: DedupItemCandidateModel) => {
		if (!primaryItemId || !candidate.item?.id) {
			return;
		}

		setDeletingCandidateId(candidate.id);
		deleteDedupItemPairMutation.mutate(
			{
				primaryItemId,
				secondaryItemId: candidate.item.id,
			},
			{
				onSettled: () => setDeletingCandidateId(null),
			},
		);
	};

	const isDeletingCandidate = (candidateId: string) => deleteDedupItemPairMutation.isPending && deletingCandidateId === candidateId;

	const getCandidateByItem = (item: ItemModel) => {
		return duplicates.find((candidate) => candidate.item?.id === item.id || candidate.id === item.id);
	};

	const candidateOverrides = (item: ItemModel) => {
		const candidate = getCandidateByItem(item);
		if (!candidate) {
			return {};
		}

		const candidateId = candidate.id;
		return {
			tag: {
				label: `Similarity: ${formatSimilarityLabel(candidate.similarity)}`,
				colorScheme: getSimilarityColorScheme(candidate.similarity),
				variant: getSimilarityVariant(candidate.similarity),
				textStyle: {
					fontWeight: 'bold',
					color: getSimilarityColor(candidate.similarity),
				},
			},
			onRemove: isPending ? () => handleDeleteCandidate(candidate) : undefined,
			removeLabel: 'Remove Match',
			removeDisabled:
				!isPending ||
				!primaryItemId ||
				!candidate.item?.id ||
				(deleteDedupItemPairMutation.isPending && !isDeletingCandidate(candidateId)),
			isRemoving: isDeletingCandidate(candidateId),
		};
	};

	return (
		<Card variant="outline" borderRadius="lg">
			<CardHeader borderBottomWidth="1px">
				<Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4}>
					<Stack spacing={1} alignItems="flex-start">
						<Heading fontSize="md">Potential Duplicate Items</Heading>
						<HStack spacing={2}>
							<Badge colorScheme={averageColorScheme} variant={averageVariant}>
								Avg Similarity: {averageSimilarityLabel}
							</Badge>
							<Badge colorScheme={getStatusColor(dedupItem.status)}>{dedupItem.status}</Badge>
						</HStack>
					</Stack>
					{dedupItem.isPending() && (
						<HStack spacing={2}>
							<Button
								size="sm"
								variant="outline"
								colorScheme="red"
								onClick={handleReject}
								isDisabled={!primaryItemId || deleteDedupItemMutation.isPending}
								isLoading={deleteDedupItemMutation.isPending}
							>
								Reject
							</Button>
				<Button
					size="sm"
					colorScheme="blue"
					onClick={handleCreateMerge}
					isDisabled={!hasSufficientItems || !primaryItemId || createMergeMutation.isPending}
					isLoading={createMergeMutation.isPending}
				>
					Create Merge
				</Button>
						</HStack>
					)}
				</Flex>
			</CardHeader>
			<CardBody>
				<Stack spacing={6}>
					<Box>
						<Heading fontSize="sm" mb={2}>Primary Item</Heading>
						{dedupItem.item ? (
							<BasicItemCard
								item={dedupItem.item}
								onItemClick={handlePrimaryItemClick}
								onArtistClick={() => {
									navigate(NavigationService.getArtistDetailsPath(dedupItem.item.artistId, dedupItem.item.getArtistName()));
								}}
								showMetadata={true}
							/>
						) : (
							<Text fontSize="sm" color="gray.500">No primary item data available.</Text>
						)}
					</Box>
					<Divider />
					<Box>
						<Heading fontSize="sm" mb={2}>Potential Matches</Heading>
						{candidateItems.length ? (
							<ItemGrid
								items={candidateItems}
								isLoading={false}
								showFilters={false}
								gridDisplay={true}
								gridMaxHeight={candidateGridMaxHeight}
								showMetadata={true}
								itemCardOverrides={candidateOverrides}
							/>
						) : (
							<Text fontSize="sm" color="gray.500">No duplicate items found.</Text>
						)}
					</Box>
				</Stack>
			</CardBody>
		</Card>
	);
}
